/** Document Settings dialog — Tier 3 Group D Slice 1d.
 *
 *  Image → Document Settings opens this dialog. Edits the document's
 *  DPI metadata + surfaces canvas size in pixels + derived inches /
 *  cm / mm readouts. Color mode is sRGB read-only in v1 (the
 *  roadmap calls for Display P3 awareness — surfaced as a label
 *  here, but actual P3 rendering is deferred).
 *
 *  This dialog is metadata-only: it never resizes the canvas. Image
 *  Size (changing pixel dimensions) is a separate Tier 4 feature. */

import { useEffect, useRef, useState } from 'react';
import { ImageIcon, X } from 'lucide-react';
import { MODAL_OVERLAY, MODAL_SHEET_CHROME } from '../lib/modal-classes';
import { DPI_PRESETS, pxToCm, pxToInches, pxToMm, type DpiPreset } from '../lib/dpi-conversion';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Current canvas pixel dimensions. */
  width: number;
  height: number;
  /** Current DPI value (or `null` if unspecified). */
  dpi: number | null;
  /** Called when the user commits a new DPI. Pass `null` to clear. */
  onDpiChanged: (dpi: number | null) => void;
}

type DpiSelection = DpiPreset | 'custom';

function classifyDpi(dpi: number | null): DpiSelection {
  if (dpi === null) return 72;
  if ((DPI_PRESETS as readonly number[]).includes(dpi)) return dpi as DpiPreset;
  return 'custom';
}

export function DocumentSettingsDialog({ open, onClose, width, height, dpi, onDpiChanged }: Props) {
  const [selection, setSelection] = useState<DpiSelection>(() => classifyDpi(dpi));
  const [customDraft, setCustomDraft] = useState<string>(() =>
    classifyDpi(dpi) === 'custom' ? String(dpi) : '',
  );

  // Re-sync ONLY when the dialog re-opens against a new document.
  // We intentionally do NOT re-classify on every `dpi` change: a
  // user typing `72` into the Custom field would otherwise see the
  // selection snap to the 72 preset and the Custom input unmount
  // mid-edit. Keying the effect on `open` confines the resync to
  // dialog-open transitions where it's actually wanted (e.g.
  // opening the dialog after picking a different document tab).
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      const next = classifyDpi(dpi);
      setSelection(next);
      setCustomDraft(next === 'custom' ? String(dpi) : '');
    }
    prevOpenRef.current = open;
  }, [open, dpi]);

  // Close on Escape — matches the rest of the editor's modals.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const effective = selection === 'custom' ? Number(customDraft) || null : (selection as DpiPreset);

  const onPickPreset = (preset: DpiPreset) => {
    setSelection(preset);
    onDpiChanged(preset);
  };

  const onPickCustom = () => {
    setSelection('custom');
    const parsed = Number(customDraft);
    if (parsed > 0 && Number.isFinite(parsed)) onDpiChanged(parsed);
  };

  const onCustomChange = (value: string) => {
    setCustomDraft(value);
    if (selection === 'custom') {
      const parsed = Number(value);
      if (parsed > 0 && Number.isFinite(parsed)) onDpiChanged(parsed);
    }
  };

  return (
    <div className={MODAL_OVERLAY} onClick={onClose} role="presentation">
      <div
        role="dialog"
        aria-label="Document settings"
        aria-modal="true"
        className={`bg-background shadow-xl ${MODAL_SHEET_CHROME} w-full sm:w-[92vw] max-w-md max-h-[90vh] flex flex-col overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
          <ImageIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-base font-semibold flex-1">Document Settings</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-4">
          <section aria-labelledby="canvas-size-label" className="space-y-1.5">
            <h3
              id="canvas-size-label"
              className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide"
            >
              Canvas size
            </h3>
            <table className="w-full text-sm tabular-nums">
              <tbody>
                <UnitRow label="Pixels" value={`${width} × ${height} px`} />
                <UnitRow
                  label="Inches"
                  value={`${pxToInches(width, effective).toFixed(2)} × ${pxToInches(height, effective).toFixed(2)} in`}
                />
                <UnitRow
                  label="Centimeters"
                  value={`${pxToCm(width, effective).toFixed(2)} × ${pxToCm(height, effective).toFixed(2)} cm`}
                />
                <UnitRow
                  label="Millimeters"
                  value={`${pxToMm(width, effective).toFixed(1)} × ${pxToMm(height, effective).toFixed(1)} mm`}
                />
              </tbody>
            </table>
            <p className="text-[11px] text-muted-foreground italic">
              Pixel dimensions are read-only here; resize lives under a separate Image Size dialog
              (planned for Tier 4).
            </p>
          </section>

          <section aria-labelledby="dpi-label" className="space-y-2">
            <h3
              id="dpi-label"
              className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide"
            >
              Resolution (DPI)
            </h3>
            <DpiRadioGroup
              selection={selection}
              onPickPreset={onPickPreset}
              onPickCustom={onPickCustom}
            />
            {selection === 'custom' && (
              <label className="flex items-center gap-2">
                <span className="text-xs w-12 text-muted-foreground">DPI</span>
                <input
                  type="number"
                  min={1}
                  max={9999}
                  value={customDraft}
                  onChange={(e) => onCustomChange(e.target.value)}
                  className="flex-1 text-sm bg-background border border-border rounded px-2 py-1 focus:border-primary focus:outline-none"
                  aria-label="Custom DPI"
                />
              </label>
            )}
            <p className="text-[11px] text-muted-foreground">
              Affects PDF export page size and the physical-unit readouts above. 72 is web default;
              300 is the common print standard.
            </p>
          </section>

          <section aria-labelledby="color-mode-label" className="space-y-1.5">
            <h3
              id="color-mode-label"
              className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide"
            >
              Color mode
            </h3>
            <div className="flex items-center justify-between text-sm">
              <span>sRGB</span>
              <span className="text-[11px] text-muted-foreground italic">
                Display P3 awareness coming later
              </span>
            </div>
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          <button
            type="button"
            onClick={() => {
              onDpiChanged(null);
              setSelection(72);
              setCustomDraft('');
            }}
            className="text-sm px-3 py-1.5 rounded-md hover:bg-accent text-muted-foreground"
          >
            Clear DPI
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function UnitRow({ label, value }: { label: string; value: string }) {
  return (
    <tr>
      <td className="py-0.5 text-muted-foreground w-32">{label}</td>
      <td className="py-0.5 text-right">{value}</td>
    </tr>
  );
}

/** A proper radiogroup: single tab stop with arrow keys cycling
 *  selection. Without this the four DPI presets each take their
 *  own tab stop and offer no arrow-key navigation, which violates
 *  the ARIA radio pattern. */
function DpiRadioGroup({
  selection,
  onPickPreset,
  onPickCustom,
}: {
  selection: DpiSelection;
  onPickPreset: (preset: DpiPreset) => void;
  onPickCustom: () => void;
}) {
  const options: DpiSelection[] = [...DPI_PRESETS, 'custom'];
  const focusedIndex = Math.max(
    0,
    options.findIndex((o) => o === selection),
  );

  const pick = (next: DpiSelection) => {
    if (next === 'custom') onPickCustom();
    else onPickPreset(next);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let next = focusedIndex;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (focusedIndex + 1) % options.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
      next = (focusedIndex - 1 + options.length) % options.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = options.length - 1;
    else return;
    e.preventDefault();
    pick(options[next]!);
  };

  return (
    <div
      role="radiogroup"
      aria-labelledby="dpi-label"
      className="grid grid-cols-4 gap-1.5"
      onKeyDown={onKeyDown}
    >
      {options.map((option) => {
        const isSelected = option === selection;
        const label = option === 'custom' ? 'Custom' : String(option);
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={isSelected}
            // Roving tabindex: only the active option is in the tab
            // order. Tab/Shift-Tab moves into and out of the group;
            // arrows move within.
            tabIndex={isSelected ? 0 : -1}
            onClick={() => pick(option)}
            className={`text-sm px-2 py-1.5 rounded-md border ${
              isSelected
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border hover:bg-accent text-muted-foreground'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
